import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Divider } from "@material-ui/core";
import { savePaymentMethod } from "../redux/brandCartSlice";
import { paymentMethodFor } from "../config/market";
import paymentBackgroundStyle from "./paymentBackground";
import styled from "styled-components";
import Layout from "../components/Global/Layout";
import CheckoutStepper from "../components/CheckoutStepper";
import { resolveProductPaymentPolicy } from "../utils/productPaymentPolicy";
import useCodPolicy from "../hooks/useCodPolicy";

const PaymentOptionButton = styled.button`
  border: none;
  padding: 15px 20px;
  background-color: teal;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 4px;
  &:disabled {
    background-color: #ccc;
    color: #aaa;
    font-weight: 300;
    font-size: 14px;
    cursor: not-allowed;
  }
`;

const ALL_PAYMENT_OPTIONS = [
    {
        key: "MOMO",
        label: "MTN Mobile Money",
        imageUrl: "https://res.cloudinary.com/lusuku/image/upload/v1693301528/Lusuku_Public_Resources/Mtn_Momo_twnyoa.svg",
    },
    {
        // "Airtel", not "AIRTEL": this key is dispatched to redux and then
        // compared verbatim in BrandPlaceOrderScreen (`paymentMethod === "Airtel"`)
        // and in config/market.js. With the upper-case spelling the place-order
        // screen rendered no pay button at all — and for a product with
        // requiredPaymentMethod "AIRTEL" that was the only selectable option, so
        // the order could never be placed.
        key: "Airtel",
        label: "Airtel Money",
        imageUrl: "https://res.cloudinary.com/lusuku/image/upload/v1693304179/Lusuku_Public_Resources/airtel_money_lusuku_htjiy7.png",
    },
    {
        key: "VISA",
        label: "Credit Card",
        imageUrl: "https://res.cloudinary.com/lusuku/image/upload/v1693302551/Lusuku_Public_Resources/Visa_Inc.-Logo.lusuku_xrhhuw.svg",
    },
    {
        key: "COD",
        label: "Cash On Delivery",
        imageUrl: "https://res.cloudinary.com/lusuku/image/upload/v1693307048/Lusuku_Public_Resources/cod_lusuku_mygqcm.png",
    },
    // Market-conditional: Zamtel Kwacha exists only where the active market's
    // registry offers it (Zambia). UG renders exactly its original four.
    ...(paymentMethodFor("ZAMTEL")
        ? [{ key: "ZAMTEL", label: paymentMethodFor("ZAMTEL").label, imageUrl: "" }]
        : []),
];

const BrandPaymentScreen = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { id } = useParams();

    const currentBrand = useSelector((state) => state.brandCart.currentBrand || id);
    const cart = useSelector((state) => state.brandCart.carts[currentBrand] || {});
    const products = Object.values(cart);

    /* The live platform COD ceiling. Until it has loaded this is null and
       the policy below leaves Cash on delivery exactly where it was — see
       resolveProductPaymentPolicy for why a shipped constant would be worse
       than no ceiling at all. */
    const { maxProductAmount: codMaxProductAmount } = useCodPolicy();
    const paymentPolicy = resolveProductPaymentPolicy(products, ALL_PAYMENT_OPTIONS, {
      codMaxProductAmount,
    });
    const paymentOptions = paymentPolicy.options;

    const [selectedOption, setSelectedOption] = useState(-1);

    useEffect(() => {
        if (products.length === 0) {
            // Guard the null-brand case (e.g. after a full reload) — otherwise
            // this produced /brand/shop/null.
            history.push(currentBrand ? `/brand/shop/${currentBrand}` : "/cart");
        }
    }, [history, products, currentBrand]);

    // Auto-select when only one option is available
    useEffect(() => {
        if (selectedOption >= paymentOptions.length) setSelectedOption(-1);
        if (selectedOption === -1 && paymentOptions.length === 1) setSelectedOption(0);
    }, [paymentOptions.length, selectedOption]);

    const submitHandler = (e) => {
        e.preventDefault();
        dispatch(savePaymentMethod(paymentOptions[selectedOption].key));
        history.push(`/brand-placeorder/${currentBrand}`);
    };

    const handleOptionChange = (index) => {
        setSelectedOption(index);
    };

    const optionStyle = (isSelected) => ({
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "12px 16px",
        borderRadius: "4px",
        cursor: "pointer",
        backgroundColor: isSelected ? "#DBF3FA" : "#EDF2F7",
        boxShadow: isSelected ? "0px 2px 5px rgba(0, 0, 0, 0.1)" : "none",
        transition: "all 0.3s",
        textAlign: "center",
        fontWeight: isSelected ? "bold" : "normal",
        marginBottom: "16px",
        height: "60px",
    });

    return (
        <Layout>
            <div
                style={{
                    width: "100vw",
                    minHeight: "100vh",
                    ...paymentBackgroundStyle,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "80px 20px 20px 20px",
                }}
            >
                <div
                    style={{
                        maxWidth: "500px",
                        width: "80%",
                        padding: "20px",
                        backgroundColor: "white",
                        borderRadius: "10px",
                        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.9)",
                    }}
                >
                    <CheckoutStepper currentStep="payment" />
                    <h1
                        style={{ fontSize: "24px", fontWeight: "300", textAlign: "center" }}
                    >
                        PAYMENT OPTIONS
                    </h1>
                    <Divider />
                    <form>
                        <label
                            style={{
                                display: "block",
                                marginBottom: "10px",
                                marginTop: "20px",
                                fontSize: "16px",
                                fontStyle: "italic",
                                color: "#4A4A4A",
                            }}
                        >
                            Select Payment Method
                        </label>
                        {paymentPolicy.notice && (
                            <div style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 10,
                                background: "#fffbeb",
                                border: "1px solid #fcd34d",
                                borderRadius: 8,
                                padding: "10px 14px",
                                marginBottom: 16,
                                fontSize: "0.83rem",
                                color: "#92400e",
                                lineHeight: 1.5,
                            }}>
                                <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>⚠️</span>
                                <span>
                                    <strong>Payment requirement.</strong> {paymentPolicy.notice}
                                </span>
                            </div>
                        )}
                        {paymentOptions.map(({ key, label, imageUrl }, index) => (
                            <div
                                key={index}
                                style={optionStyle(selectedOption === index)}
                                onClick={() => handleOptionChange(index)}
                            >
                                {!imageUrl ? (
                                    <div style={{ width: "50px", height: "44px", marginRight: "5px" }} />
                                ) : (
                                <img
                                    src={imageUrl}
                                    alt={label}
                                    style={{ width: "50px", height: "44px", marginRight: "5px" }}
                                />
                                )}
                                <div
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        borderRadius: "50%",
                                        border:
                                            selectedOption === index
                                                ? "3px solid #3f83f8"
                                                : "1px solid #D1D5DB",
                                        backgroundColor:
                                            selectedOption === index ? "#3f83f8" : "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginRight: "8px",
                                    }}
                                >
                                    {selectedOption === index && (
                                        <div
                                            style={{
                                                width: "8px",
                                                height: "8px",
                                                borderRadius: "50%",
                                                backgroundColor: "white",
                                            }}
                                        ></div>
                                    )}
                                </div>
                                {label}
                            </div>
                        ))}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <PaymentOptionButton
                                variant="contained"
                                color="primary"
                                disabled={selectedOption === -1}
                                style={{
                                    width: "150px",
                                    marginTop: "10px",
                                }}
                                onClick={submitHandler}
                            >
                                Continue
                            </PaymentOptionButton>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default BrandPaymentScreen;
